/* eslint-disable @typescript-eslint/no-explicit-any */

import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { Dialog } from 'primereact/dialog';

import { useToastNotification } from '@/hooks';
import { Button, LoadingSpinner } from '@/components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { Value } from '@/types/getPendingPaymentsType';

import { deletePendingPayment } from '@/services/deletePendingPayments';
import styles from './DeletePendingPaymentsModal.module.scss';
import { PendingPaymentsFiltersType } from './PendingPaymentsFilters';

type DeletePendingPaymentsModalProps = {
  paymentsOids: number[];
  rowData?: Value;
  filters: PendingPaymentsFiltersType;
  getData: any;
};

const DeletePendingPaymentsModal: FC<DeletePendingPaymentsModalProps> = ({
  paymentsOids,
  rowData,
  filters,
  getData,
}) => {
  const { t } = useTranslation('pendingPayments');
  const { showToast } = useToastNotification();
  const [deleteModalIsOpened, setDeleteModalIsOpened] = useState(false);

  const deletePaymentsSelected = useCallback(async () => {
    const promises = paymentsOids.map((oid) =>
      deletePendingPayment({ oidLiquidacion: oid })
    );

    return Promise.allSettled(promises);
  }, [paymentsOids]);

  const toggleDialog = () => {
    setDeleteModalIsOpened(false);
  };

  const { mutate: deletePayments, isPending: isLoading } = useMutation({
    mutationFn: deletePaymentsSelected,
    onSuccess: (res): void => {
      const count = res.filter(({ status }) => status === 'fulfilled').length;
      const allDeleted = count === paymentsOids.length;

      if (count) {
        showToast({
          severity: 'success',
          detail: t('deletePendingPaymentsSuccess', { count }),
        });
      }

      if (allDeleted) {
        setDeleteModalIsOpened(true);
        return;
      }

      const errors = res
        .filter(
          (item): item is PromiseRejectedResult => item?.status === 'rejected'
        )
        .map(({ reason }) => reason);

      errors.forEach((error) =>
        showToast({
          severity: 'error',
          detail: error?.message,
        })
      );
    },
    onSettled: () => {
      toggleDialog();
      getData(filters);
    },
    onError: (error) =>
      showToast({
        severity: 'error',
        detail: error?.message,
      }),
  });

  const dialogHeader = (
    <div>
      <FontAwesomeIcon icon={faTrash} style={{ marginRight: '0.5rem' }} />
      {t('deletePendingPayments')}
    </div>
  );

  const onDeleteButtonClick = () => {
    deletePayments();
  };

  const footerContent = (
    <div className="w-full flex justify-content-end ">
      <Button
        label={t('cancel')}
        link
        onClick={toggleDialog}
        disabled={isLoading}
        style={{ color: '#170a33', width: '100px' }}
      />
      <Button
        style={{ backgroundColor: '#FF0000', color: '#FFFFFF', width: '100px' }}
        label={t('delete')}
        onClick={onDeleteButtonClick}
        loading={isLoading}
      />
    </div>
  );

  return (
    <>
      <button
        type="button"
        className={styles.secondaryActionsButtons}
        onClick={() => setDeleteModalIsOpened(!deleteModalIsOpened)}
        disabled={rowData?.estado !== 'En Edición'}
      >
        {isLoading && <LoadingSpinner size="tiny" />}
        {!isLoading && <FontAwesomeIcon icon={faTrash} />}
      </button>
      <Dialog
        header={dialogHeader}
        visible={deleteModalIsOpened}
        style={{ position: 'relative', width: '90%', maxWidth: '500px' }}
        onHide={toggleDialog}
        footer={footerContent}
      >
        {isLoading && (
          <div
            style={{ zIndex: 10, background: 'rgba(0,0,0, 0.2)' }}
            className="absolute top-0 bottom-0 left-0 right-0 z-10 flex align-items-center justify-content-center"
          >
            <LoadingSpinner />
          </div>
        )}
        <div className="px-6 pb-6">
          <p>{t('deletePendingPaymentsMessage')}</p>
        </div>
      </Dialog>
    </>
  );
};

export default DeletePendingPaymentsModal;
